import { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  InputAdornment,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Search,
  Download,
  Visibility,
  Edit,
  Lock,
  LockOpen,
} from '@mui/icons-material';
import { mockClosures } from './posData';

// Altura mínima para filas táctiles
const TOUCH_ROW_HEIGHT = 56;

const POSClosures = () => {
  const [searchTerm, setSearchTerm] = useState('');

  // Responsive
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  // Filtrar cierres según búsqueda
  const filteredClosures = useMemo(() => {
    let result = [...mockClosures];

    if (searchTerm) {
      result = result.filter(
        (c) =>
          c.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
          c.responsable.toLowerCase().includes(searchTerm.toLowerCase()) ||
          c.fechaApertura.includes(searchTerm) ||
          c.fechaCierre.includes(searchTerm)
      );
    }

    return result;
  }, [searchTerm]);

  // Calcular totales
  const totals = useMemo(() => {
    return filteredClosures.reduce(
      (acc, c) => {
        acc.ingresos += c.ingresos;
        acc.egresos += c.egresos;
        acc.total += c.total;
        return acc;
      },
      { ingresos: 0, egresos: 0, total: 0 }
    );
  }, [filteredClosures]);

  const getEstadoColor = (estado) => {
    switch (estado) {
      case 'cerrado':
        return 'success';
      case 'abierto':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getTotalColor = (total) => {
    if (total > 0) return 'success.main';
    if (total < 0) return 'error.main';
    return 'text.primary';
  };

  // Columnas a mostrar según breakpoint
  const visibleColumns = isMobile ? ['id', 'responsable', 'total', 'estado'] :
                        isTablet ? ['id', 'fechaApertura', 'responsable', 'total', 'estado'] :
                        ['id', 'fechaApertura', 'fechaCierre', 'responsable', 'ingresos', 'egresos', 'total', 'estado'];

  return (
    <Box>
      {/* Barra de filtros */}
      <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
          {/* Buscador */}
          <TextField
            size="small"
            placeholder={isMobile ? "Buscar..." : "Buscar por responsable, fecha..."}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: isMobile ? 150 : 250, flexGrow: 1 }}
          />

          {/* Selector de fecha - solo en desktop */}
          {!isMobile && (
            <>
              <TextField
                type="date"
                size="small"
                label="Desde"
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 150 }}
              />

              <TextField
                type="date"
                size="small"
                label="Hasta"
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 150 }}
              />

              {/* Botón de descarga */}
              <Button variant="outlined" startIcon={<Download />}>
                Descargar
              </Button>
            </>
          )}
        </Box>
      </Paper>

      {/* Totales - responsive */}
      <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, mb: 2, flexWrap: 'wrap' }}>
        <Paper sx={{ p: { xs: 1.5, sm: 2 }, flex: '1 1 auto', minWidth: { xs: '100%', sm: 200 } }}>
          <Typography variant="subtitle1" color="text.secondary">
            Total Ingresos
          </Typography>
          <Typography variant="h5" color="success.main" sx={{ fontWeight: 'bold' }}>
            ${totals.ingresos.toFixed(2)}
          </Typography>
        </Paper>
        <Paper sx={{ p: { xs: 1.5, sm: 2 }, flex: '1 1 auto', minWidth: { xs: '45%', sm: 200 } }}>
          <Typography variant="subtitle1" color="text.secondary">
            Total Egresos
          </Typography>
          <Typography variant="h5" color="error.main" sx={{ fontWeight: 'bold' }}>
            ${totals.egresos.toFixed(2)}
          </Typography>
        </Paper>
        <Paper sx={{ p: { xs: 1.5, sm: 2 }, flex: '1 1 auto', minWidth: { xs: '45%', sm: 200 } }}>
          <Typography variant="subtitle1" color="text.secondary">
            Balance Total
          </Typography>
          <Typography
            variant="h5"
            color={getTotalColor(totals.total)}
            sx={{ fontWeight: 'bold' }}
          >
            ${totals.total.toFixed(2)}
          </Typography>
        </Paper>
      </Box>

      {/* Grid de cierres */}
      <TableContainer 
        component={Paper}
        sx={{ 
          // Scroll horizontal en móvil
          overflowX: 'auto',
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'grey.100' }}>
              {visibleColumns.includes('id') && <TableCell sx={{ fontWeight: 'bold' }}>ID</TableCell>}
              {visibleColumns.includes('fechaApertura') && <TableCell sx={{ fontWeight: 'bold' }}>Fecha Apertura</TableCell>}
              {visibleColumns.includes('fechaCierre') && <TableCell sx={{ fontWeight: 'bold' }}>Fecha Cierre</TableCell>}
              {visibleColumns.includes('responsable') && <TableCell sx={{ fontWeight: 'bold' }}>Responsable</TableCell>}
              {visibleColumns.includes('ingresos') && <TableCell sx={{ fontWeight: 'bold' }} align="right">Ingresos</TableCell>}
              {visibleColumns.includes('egresos') && <TableCell sx={{ fontWeight: 'bold' }} align="right">Egresos</TableCell>}
              {visibleColumns.includes('total') && <TableCell sx={{ fontWeight: 'bold' }} align="right">Total</TableCell>}
              {visibleColumns.includes('estado') && <TableCell sx={{ fontWeight: 'bold' }}>Estado</TableCell>}
              <TableCell sx={{ fontWeight: 'bold' }} align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredClosures.length === 0 ? (
              <TableRow>
                <TableCell colSpan={visibleColumns.length + 1} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No se encontraron cierres de turno
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredClosures.map((closure) => (
                <TableRow 
                  key={closure.id}
                  sx={{ 
                    // Eliminar hover para touch - usar active
                    cursor: 'pointer',
                    minHeight: TOUCH_ROW_HEIGHT,
                    '&:active': {
                      backgroundColor: 'action.selected',
                    },
                    transition: 'background-color 0.15s ease',
                  }}
                >
                  {visibleColumns.includes('id') && (
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {closure.id}
                      </Typography>
                    </TableCell>
                  )}
                  {visibleColumns.includes('fechaApertura') && (
                    <TableCell>
                      <Typography variant="body2">{closure.fechaApertura}</Typography>
                      {!isMobile && (
                        <Typography variant="caption" color="text.secondary">
                          {closure.horaApertura}
                        </Typography>
                      )}
                    </TableCell>
                  )}
                  {visibleColumns.includes('fechaCierre') && (
                    <TableCell>
                      <Typography variant="body2">{closure.fechaCierre}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {closure.horaCierre}
                      </Typography>
                    </TableCell>
                  )}
                  {visibleColumns.includes('responsable') && (
                    <TableCell>
                      <Typography variant="body2">{closure.responsable}</Typography>
                    </TableCell>
                  )}
                  {visibleColumns.includes('ingresos') && (
                    <TableCell align="right">
                      <Typography variant="body2" color="success.main" sx={{ fontWeight: 'bold' }}>
                        ${closure.ingresos?.toFixed(2) || closure.incomes?.toFixed(2) || '0.00'}
                      </Typography>
                    </TableCell>
                  )}
                  {visibleColumns.includes('egresos') && (
                    <TableCell align="right">
                      <Typography variant="body2" color="error.main" sx={{ fontWeight: 'bold' }}>
                        ${closure.egresos?.toFixed(2) || '0.00'}
                      </Typography>
                    </TableCell>
                  )}
                  {visibleColumns.includes('total') && (
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 'bold',
                          color: getTotalColor(closure.total),
                        }}
                      >
                        ${closure.total?.toFixed(2) || '0.00'}
                      </Typography>
                    </TableCell>
                  )}
                  {visibleColumns.includes('estado') && (
                    <TableCell>
                      <Chip
                        icon={closure.estado === 'cerrado' ? <Lock /> : <LockOpen />}
                        label={closure.estado === 'cerrado' ? 'Cerrado' : 'Abierto'}
                        color={getEstadoColor(closure.estado)}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                  )}
                  <TableCell align="center">
                    {/* Botones táctiles grandes */}
                    <Tooltip title="Ver Detalle">
                      <IconButton 
                        size="medium"
                        sx={{ 
                          minWidth: 48, 
                          minHeight: 48,
                        }}
                      >
                        <Visibility />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Editar">
                      <IconButton 
                        size="medium"
                        sx={{ 
                          minWidth: 48, 
                          minHeight: 48,
                        }}
                      >
                        <Edit />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default POSClosures;
